import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

export const styles = (theme: Theme) =>
	({
		// Grow into the layout's flex column so the sticky save bar sits at the bottom of the window on
		// short tabs. A fixed calc(100vh - 220px) left it floating above the bottom (MAX-4507).
		root: {
			display: "flex",
			flexDirection: "column",
			flex: "1 0 auto",
		},
		content: {
			flex: "1 0 auto",
		},
		// Tertiary button style (MAX-3862): SF Pro Semibold 16px in the teal tertiary-action color,
		// no left/right padding so the label aligns left with the profile card below.
		backLink: {
			display: "inline-flex",
			alignItems: "center",
			gap: 4,
			padding: 0,
			// 24px above/below per Figma (the page container already contributes 16px above).
			marginTop: 8,
			marginBottom: theme.spacing(3),
			fontSize: 16,
			fontWeight: 600,
			lineHeight: "20px",
			color: theme.palette.custom.tertiaryAction,
			cursor: "pointer",
			"&:hover": {
				textDecoration: "none",
			},
		},
		layout: {
			display: "flex",
			gap: theme.spacing(3),
			alignItems: "flex-start",
			[theme.breakpoints.down("sm")]: {
				flexDirection: "column",
			},
		},
		leftPanel: {
			width: 280,
			flexShrink: 0,
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.custom.borderCard}`,
			borderRadius: 8,
			padding: theme.spacing(3),
			[theme.breakpoints.down("sm")]: {
				width: "100%",
			},
		},
		rightPanel: {
			flex: "1 1 auto",
			minWidth: 0,
		},
		// No full-width border: the baseline is each tab's own underline segment, so the line spans
		// only the tab group. On mobile the tabs can overflow, so scroll and left-align.
		tabBar: {
			display: "flex",
			justifyContent: "center",
			marginBottom: theme.spacing(3),
			[theme.breakpoints.down("sm")]: {
				justifyContent: "flex-start",
				overflowX: "auto",
				WebkitOverflowScrolling: "touch",
			},
		},
		tab: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 6,
			padding: 0,
			flexShrink: 0, // keep tabs from compressing when the row scrolls on mobile
			cursor: "pointer",
			fontFamily: theme.typography.fontFamily,
			fontSize: 16,
			fontWeight: 400,
			lineHeight: "24px",
			color: theme.palette.custom.textTertiary,
			background: "none",
			border: "none",
			outline: "none",
		},
		// 16px horizontal padding lives on the label only, so the underline below can span the tab's
		// full width and the adjacent segments connect into one continuous baseline.
		tabLabel: {
			padding: "0 16px",
		},
		tabActive: {
			color: theme.palette.custom.textPrimary,
		},
		// Grey baseline by default; the active tab's segment turns orange. Adjacent tabs abut with no
		// gap, so the per-tab segments form one continuous line under the whole tab group.
		tabUnderline: {
			width: "100%",
			height: 2,
			backgroundColor: theme.palette.custom.borderCard,
		},
		tabUnderlineActive: {
			backgroundColor: theme.palette.custom.primary,
		},
		section: {
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.custom.borderCard}`,
			borderRadius: 8,
			padding: theme.spacing(3),
			marginBottom: theme.spacing(2),
		},
		// Main section headers: 18px (MAX-3867). Subsection headers use 16px (see each tab's subTitle).
		sectionTitle: {
			fontSize: 18,
			lineHeight: "24px",
			fontWeight: 700,
			color: theme.palette.custom.textPrimary,
			marginBottom: theme.spacing(2),
		},
		// Responsive max-width so the form doesn't strand whitespace on wide screens.
		formColumn: {
			width: "100%",
			boxSizing: "border-box",
			[theme.breakpoints.up("sm")]: {
				maxWidth: 520,
			},
			[theme.breakpoints.up("md")]: {
				maxWidth: 576,
			},
			[theme.breakpoints.up("lg")]: {
				maxWidth: 720,
			},
			[theme.breakpoints.up("xl")]: {
				maxWidth: 840,
			},
		},
		// Descriptions use primary text color everywhere (MAX-3866).
		sectionDescription: {
			color: theme.palette.custom.textPrimary,
			marginBottom: theme.spacing(2),
		},
		stickyBar: {
			position: "sticky",
			bottom: 0,
			display: "flex",
			justifyContent: "flex-end",
			gap: theme.spacing(1),
			padding: theme.spacing(2),
			backgroundColor: theme.palette.background.paper,
			borderTop: `1px solid ${theme.palette.custom.borderCard}`,
			marginTop: theme.spacing(2),
		},
		avatarBlock: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			textAlign: "center",
			gap: 10, // Figma: 10px between avatar, name, and bio link
		},
		// Relative wrapper so the edit-photo badge can sit on the avatar's bottom-right (Figma 707:10875).
		avatarWrap: {
			position: "relative",
			display: "inline-flex",
		},
		editPhotoBadge: {
			position: "absolute",
			right: 0,
			bottom: 6, // sit just inside the circle's lower-right, matching Figma
			width: 24,
			height: 24,
			padding: 0,
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.common.white,
			"&:hover": { backgroundColor: theme.palette.primary.dark },
		},
		memberName: {
			fontWeight: 700,
			fontSize: 24,
			lineHeight: "32px",
			overflowWrap: "anywhere",
		},
		viewBioLink: {
			display: "inline-flex",
			alignItems: "center",
			gap: 4,
			color: theme.palette.primary.main,
			fontSize: 14,
		},
		// Each field block: label + value, 28px label-to-value baseline, 24px between blocks (Figma).
		summaryField: {
			marginTop: theme.spacing(3),
		},
		summaryLabel: {
			color: theme.palette.custom.textSecondary,
			fontSize: 14,
			lineHeight: "20px",
			marginBottom: 4,
		},
		summaryValue: {
			color: theme.palette.custom.textPrimary,
			fontSize: 16,
			lineHeight: "24px",
			// Long unbroken values (emails, URLs) wrap inside the fixed-width panel (MAX-4382).
			overflowWrap: "anywhere",
		},
		// 24px above; the following field's own marginTop supplies the 24px below (avoids doubling).
		summaryDivider: {
			marginTop: theme.spacing(3),
		},
		fieldLabelAbove: {
			display: "block",
			marginBottom: 8,
			fontSize: 14,
			lineHeight: "20px",
			color: theme.palette.custom.textSecondary,
		},
		languagesSelect: {
			// Let the theme's MuiAutocomplete rule own padding/arrow position so the caret aligns
			// with the Select fields; only the border color is set here.
			"& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.custom.borderCard },
		},
		languageChip: {
			height: 32,
			borderRadius: 16,
			backgroundColor: "#EBEBEB",
			color: theme.palette.custom.textPrimary,
			fontSize: 14,
			margin: 4,
		},
		pillRow: {
			display: "flex",
			flexWrap: "wrap",
			gap: 4,
		},
		// Cap the reused BioTab's fields to the form's responsive width.
		bioPage: {
			// The legacy BioTab's non-Enhanced branch nests a spacing={2} Grid container inside a
			// spacing={1} one. MUI applies negative margins for spacing, and nested containers
			// compound them, pulling the fields left of the section heading. Neutralise the outer
			// container's offset so the content lines up with the title above it (MAX-4139).
			"& > .MuiGrid-container > .MuiGrid-container": {
				width: "100%",
				marginLeft: 0,
				marginTop: 0,
			},
			"& > .MuiGrid-container": {
				width: "100%",
				[theme.breakpoints.up("sm")]: { maxWidth: 520 },
				[theme.breakpoints.up("md")]: { maxWidth: 576 },
				[theme.breakpoints.up("lg")]: { maxWidth: 720 },
				[theme.breakpoints.up("xl")]: { maxWidth: 840 },
			},
		},
		mlsRow: {
			marginBottom: theme.spacing(1),
		},
		mlsRemoveButton: {
			color: theme.palette.error.main,
		},
		// Push the button down past the label so it centers on the input field.
		mlsRemove: {
			display: "flex",
			[theme.breakpoints.up("sm")]: {
				marginTop: 32,
			},
		},
	} satisfies Record<string, CSSObject>);
